<template>
  <layout-navigation>
    <section :class="[$style.page, $style.fit]">
      <header :class="$style.header">
        <div :class="$style.control">
          <store-based-date-time-picker :clearable="false" />
          <div v-if="isBarChart" :class="$style.control">
            <div :class="$style.box" @click="changeMode(false)">
              <img :class="$style.arrow" src="@/assets/images/icons/navigation/menu.svg">
            </div>
            <div :class="[$style.box, $style.activeBox]">
              <img :class="$style.arrow" src="@/assets/images/icons/navigation/menu-rotated-selected.svg">
            </div>
          </div>
          <div v-else :class="[$style.control, { [$style.hide]: !hide }]">
            <div :class="[$style.box, $style.activeBox]">
              <img :class="$style.arrow" src="@/assets/images/icons/navigation/menu-selected.svg">
            </div>
            <div :class="$style.box" @click="changeMode(true)">
              <img :class="$style.arrow" src="@/assets/images/icons/navigation/menu-rotated.svg">
            </div>
          </div>
        </div>

        <div :class="$style.control">
          <refresh :class="$style.mr" :text="$t('global.refreshData')" @click="refresh" />
        </div>
      </header>

      <div :class="$style.list">
        <button :class="$style.btnList" @click="isOpened = !isOpened">
          <div :class="$style.modeName">
            {{ currentMode }}
          </div>
          <span :class="{ [$style.caretClosed]: !isOpened }">
            <img src="@/assets/images/icons/computer/screenshots/additional-angle.svg">
          </span>
        </button>
        <div v-if="isOpened" :class="[$style.mainContainer, { [$style.hide]: !hide }]">
          <div :class="$style.container">
            <div class="searchLogs">
              <treeselect
                v-model="treeSelectComputers"
                :multiple="true"
                :options="computers"
                :clearable="true"
                :open-on-click="true"
                :placeholder="$t('computers.userSelection')"
                value-consists-of="LEAF_PRIORITY"
                :default-expand-level="1"
                :normalizer="normalizer"
                :disable-branch-nodes="false"
              />
            </div>
            <div class="searchLogs">
              <treeselect
                v-model="treeSelectReports"
                :multiple="true"
                :options="reports"
                :clearable="true"
                :open-on-click="true"
                :placeholder="$t('computers.categorySelection')"
                value-consists-of="LEAF_PRIORITY"
                :default-expand-level="1"
                :normalizer="normalizer"
              />
            </div>
            <button
              :class="[$style.button, { [$style.disabledButton]: disabledButton }]"
              :disabled="disabledButton"
              @click="getFeed()"
            >
              {{ $t('global.show') }}
            </button>
          </div>
        </div>
      </div>

      <div :class="$style.wrapper">
        <div v-show="isTableLoading">
          <loader />
        </div>
        <div v-show="isTableReady" :class="$style.ready">
          {{ $t('computers.allEventsLoaded') }}
          <img src="@/assets/images/icons/navigation/confirm.svg" :class="$style.saveIcon">
        </div>
      </div>
      <draggable v-if="isViewDashboard" :class="$style.draggableBoard">
        <advanced-list v-for="({ data, reportId }, index) in sortedData" :key="index" :data="getReport(reportId)">
          <table v-if="!data.length" :class="$style.tableReport">
            <tr :class="$style.headerReport">
              <td>{{ $t('computers.noData') }}</td>
            </tr>
          </table>
          <bar-chart v-else-if="isBarChart" :id="index" :chart="data" :data="getReport(reportId)" />
          <table v-else :class="$style.tableReport">
            <thead>
              <tr :class="$style.headerReport">
                <td v-for="column in columns" :key="column.name" @click="changeSort(column, reportId)">
                  <div :class="$style.reportName">
                    <div>
                      {{ column.name }}
                    </div>

                    <img
                      src="@/assets/images/icons/table/sort-arrow.svg"
                      :class="[
                        $style.arrow,
                        {
                          [$style.asc]: column.sort == 2 && currentReportId == reportId,
                          [$style.hidden]: column.sort == 0,
                        },
                      ]"
                    >
                  </div>
                </td>
              </tr>
            </thead>
            <tbody v-for="(item, idx) in data" :key="idx" :class="$style.reportList">
              <tr :class="$style.reportItem">
                <td>
                  {{ item.name }}
                </td>
                <td>
                  {{ convertSecondsToDays(item.value) }}
                </td>
              </tr>
            </tbody>
          </table>
        </advanced-list>
      </draggable>
    </section>
  </layout-navigation>
</template>

<script>
import draggable from 'vuedraggable';
import AdvancedList from '@/views/computers/advanced/advancedList.vue';
import LayoutNavigation from '@/layouts/navigation.vue';
import Refresh from '@/components/common/filter/refresh.vue';
import StoreBasedDateTimePicker from '@/components/common/filter/store-based-date-time-picker.vue';
import dayjs from 'dayjs';
import Treeselect from '@riophae/vue-treeselect';
import { getSumTable } from '@/api/methods/reports/users/index';
import { getWebsiteStat, getProgramStat } from '@/api/methods/reports/statistics/index';
import { getComputers } from '@/api/methods/computers';
import Loader from '@/components/common/blocks/loader.vue';
import { convertSecondsToDays, getDateUTCFromBuildReport } from '@/helpers/format-date';
import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
import BarChart from './bar-chart.vue';

export default {
  components: {
    draggable,
    AdvancedList,
    BarChart,
    LayoutNavigation,
    Refresh,
    StoreBasedDateTimePicker,
    Treeselect,
    Loader,
  },
  data() {
    return {
      hide: true,
      isOpened: true,
      isBarChart: true,
      isViewDashboard: false,
      computersTree: [],
      webTree: [],
      productive: {
        productiveWeb: [],
        unProductiveWeb: [],
        productivePC: [],
        unProductivePC: [],
      },
      treeSelectComputers: [],
      treeSelectReports: [],
      selectedComputers: [],
      selectedReports: [],
      computers: [],
      columns: [
        {
          name: this.$t('computers.columns.computerName'),
          field: 'name',
          sort: 1,
          index: 0,
        },
        {
          name: this.$t('computers.columns.time'),
          field: 'value',
          sort: 0,
          index: 1,
        },
      ],

      reports: [
        {
          id: 0,
          name: this.$t('computers.reports.selectAll'),
          children: [
            { id: 1, name: this.$t('computers.reports.workTime'), field: 'workTime', color: '#009999' },
            { id: 2, name: this.$t('computers.reports.activeTime'), field: 'activeTime', color: '#33CCCC' },
            { id: 3, name: this.$t('computers.reports.web'), field: 'web', color: '#6C8DD5' },
            { id: 4, name: this.$t('computers.reports.productiveWeb'), field: 'productiveWeb', color: '#6A48D7' },
            {
              id: 5,
              name: this.$t('computers.reports.unProductiveWeb'),
              field: 'unProductiveWeb',
              color: '#9F3ED5',
            },
            { id: 6, name: this.$t('computers.reports.productivePC'), field: 'productivePC', color: '#AD66D5' },
            { id: 7, name: this.$t('computers.reports.unProductivePC'), field: 'unProductivePC', color: '#FF7400' },
            { id: 8, name: this.$t('computers.reports.printer'), field: 'printer', color: '#FFFF00' },
          ],
        },
      ],

      currentReportId: 0,
    };
  },
  computed: {
    isTableReady() {
      return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
    },
    isTableLoading() {
      return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
    },
    selectedData() {
      return this.selectedReports.map((reportId) => ({ data: this.getList(reportId), reportId }));
    },
    sortedData() {
      const selectedItem = this.selectedData.find((item) => this.currentReportId === item.reportId);
      if (selectedItem) {
        selectedItem.data.sort(this.compare);
      }
      return this.selectedData;
    },

    isComputers() {
      return [1, 2, 8].some((item) => this.treeSelectReports.includes(item));
    },
    isWeb() {
      return [3, 4, 5].some((item) => this.treeSelectReports.includes(item));
    },
    isStat() {
      return [6, 7].some((item) => this.treeSelectReports.includes(item));
    },
    currentMode() {
      if (this.isBarChart) return this.$t('computers.dataAsHistograms');
      return this.$t('computers.tabularData');
    },
    disabledButton() {
      return !(this.treeSelectComputers.length && this.treeSelectReports.length);
    },
  },
  async created() {
    this.whenUpdated();
    await getComputers().then(({ tree }) => {
      tree.forEach((item) => this.changeId(item));
      this.computers = tree;
    });
  },
  mounted() {
    this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
  },
  methods: {
    compare(a, b) {
      const index = this.columns.findIndex((x) => x.sort > 0);
      const key = this.columns[index].field;
      a = a[key];
      b = b[key];

      if (typeof a === 'string' && typeof b === 'string') {
        a = a.toLowerCase();
        b = b.toLowerCase();
      }

      if (this.columns[index].sort === 1) {
        if (a > b) {
          return -1;
        }

        if (a < b) {
          return 1;
        }
        return 0;
      }

      if (this.columns[index].sort === 2) {
        if (a < b) {
          return -1;
        }

        if (a > b) {
          return 1;
        }

        return 0;
      }
      return 0;
    },
    changeSort(column, reportId) {
      this.currentReportId = reportId;
      const { sort } = column;
      this.columns.forEach((col) => {
        col.sort = 0;
      });

      if (sort === 1) {
        column.sort = 2;
      } else column.sort = 1;
    },
    changeId(item) {
      if (item.type === 'folder') item.id = `folder${item.id}`;
      if (item.children) item.children.forEach((child) => this.changeId(child));
    },
    changeMode(value) {
      this.isBarChart = value;
    },
    convertSecondsToDays(secNum) {
      return convertSecondsToDays(secNum);
    },
    getReport(reportId) {
      const { children } = this.reports[0];
      return children.find(({ id }) => id === reportId);
    },
    getValue(value) {
      return !this.isBarChart ? value : (value / 3600).toFixed(1);
    },
    getList(reportId) {
      const report = this.getReport(reportId);
      const { field } = report;
      if (this.productive[field]) {
        return this.productive[field].map(({ computer, time }) => ({
          name: computer,
          value: this.getValue(time),
        }));
      }
      if (field === 'web') {
        return this.webTree.map(({ userName, activeTime }) => ({
          name: userName,
          value: this.getValue(activeTime),
        }));
      }
      return this.computersTree.map(({ nameUser, reportData }) => ({
        name: nameUser,
        value: this.getValue(reportData[field]),
      }));
    },
    getSelectedComputers(computers) {
      computers.forEach((item) => {
        if (item.children && item.children.length) {
          this.getSelectedComputers(item.children);
        }
        const isIncluded = this.treeSelectComputers.includes(item.id);
        if (isIncluded) {
          item.checked = true;
          this.selectedComputers.push(item);
        }
      });
    },

    async getFeed() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
      this.selectedComputers = [];
      this.getSelectedComputers(this.computers);

      const { dateFrom, dateTo } = getDateUTCFromBuildReport(this.$route.query.dateFrom, this.$route.query.dateTo);

      const workTimeOnly = 0;
      const routeName = 'consolidatedFull';
      if (this.isComputers) {
        await getSumTable(this.selectedComputers, dateFrom, dateTo, workTimeOnly, routeName).then(({ data }) => {
          if (data) {
            this.computersTree = data.result.computersTree;
          }
        });
      }
      if (this.isWeb) {
        await getWebsiteStat(this.selectedComputers, dateFrom, dateTo).then(({ data }) => {
          if (data) {
            this.productive.productiveWeb = data.result.productivePC;
            this.productive.unProductiveWeb = data.result.unProductivePC;
            this.webTree = data.result.users;
          }
        });
      }
      if (this.isStat) {
        await getProgramStat(this.selectedComputers, dateFrom, dateTo).then(({ data }) => {
          if (data) {
            this.productive.productivePC = data.result.productivePC;
            this.productive.unProductivePC = data.result.unProductivePC;
          }
        });
      }
      this.selectedReports = this.treeSelectReports;
      this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);
      this.isViewDashboard = true;
      this.isOpened = false;
    },
    normalizer(node) {
      let { name } = node;
      if (node.nameUser) name = node.nameUser;
      return {
        label: name,
      };
    },
    whenUpdated() {
      const { dateFrom, dateTo } = this.$route.query;
      if (dateFrom && dateTo) {
        this.$store.commit('filters/updateDateRange', [dateFrom, dateTo]);
      }

      const queryMutations = {};

      if (!dateFrom) {
        queryMutations.dateFrom = dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss');
      }

      if (!dateTo) {
        queryMutations.dateTo = dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss');
      }

      if (Object.keys(queryMutations).length) {
        this.$router.replace({
          query: {
            ...this.$route.query,
            ...queryMutations,
          },
        });
      }
    },
    async refresh() {
      await this.getFeed();
    },
  },
};
</script>

<style lang="scss" module>
  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .page {
    background: $white;
    width: 100%;
  }

  .fit {
    height: fit-content;
  }

  .header {
    box-shadow: $shadow;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    padding: 20px 30px;
  }

  .control {
    display: flex;
    align-items: flex-end;
    .mr {
      margin-right: 30px;
    }
  }

  .box {
    border: 1px solid #dfdfdf;
    height: 40px;
    display: flex;
    width: 40px;
    align-items: center;
    justify-content: center;
    margin: 0px 5px;
    background: #f5f5f5;
    cursor: pointer;
  }

  .activeBox {
    background: #e9f7ff;
    border-color: #b1d6ed;
  }

  .datePicker {
    margin-right: 40px;
  }

  .mainContainer {
    display: flex;
    flex-direction: column;
    padding: 0 20px 30px 20px;
  }

  .list {
    display: flex;
    flex-direction: column;
    background: white;
    border: 1px solid #c4c4c4;
    margin: 0 10px 10px 10px;
  }

  .btnList {
    cursor: pointer;
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 20px 20px 47px;
    background: white;
    border: none;
    font-size: 16px;
    font-weight: 500;
  }
  .caretClosed {
    transform: rotate(180deg);
  }

  .button {
    border: 1px solid white;
    cursor: pointer;
    color: white;
    height: 36px;
    width: 10%;
    background: #2985bf;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 5px 10px 0px 0px;
    min-width: 80px;
  }
  .disabledButton {
    opacity: 0.6;
  }

  .container {
    display: flex;
    gap: 5%;
    margin: 0px 10px;
  }
  .wrapper {
    display: flex;
    justify-content: center;
    padding: 10px;
  }
  .modeName {
    display: flex;
    font-weight: 500;
    align-items: center;
  }

  .saveIcon {
    width: 20px;
  }

  .ready {
    display: flex;
    gap: 5px;
    justify-content: center;
    align-items: center;
  }

  .hide {
    visibility: hidden;
  }

  .draggableBoard {
    display: grid;
    grid-template-columns: repeat(3, auto);
    column-gap: 71px;
    row-gap: 23px;
    padding: 30px 40px;
  }

  .tableReport {
    font-weight: 600;
  }

  .headerReport {
    background: #dbf1ff;
    height: 31px;
    position: sticky;
    top: -2px;
    z-index: 3;
    cursor: pointer;

    td {
      border: 1px solid $borderColor;
      align-items: center;
      text-align: center;
      color: #232326;
      letter-spacing: -0.08px;
      font-size: 13px;
      align-content: center;
    }
  }
  .reportName {
    display: flex;
    justify-content: center;
    align-items: baseline;
    gap: 5px;
  }
  .arrow {
    &.asc {
      transform: rotate(180deg);
    }

    &.hidden {
      visibility: hidden;
    }
  }
  .reportItem {
    height: 29px;
    cursor: pointer;
    border-bottom: 0.2px solid #eee;

    &:hover {
      background: $light-gray;
    }

    td {
      border-right: 1px solid $borderColor;
      border-left: 1px solid $borderColor;
      align-items: center;
      letter-spacing: -0.24px;
      color: $black;
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      text-align: center;
      padding: 0px 5px;
      align-content: center;
    }
  }
</style>

<style>
  .searchLogs {
    display: flex;
    flex-direction: column;
    margin: 5px 0px;
    width: 45%;
  }

  .searchLogs .vue-treeselect {
    margin-left: 2%;
  }

  .searchLogs .vue-treeselect__control {
    border-radius: 0px;
    width: 95%;
  }

  .searchLogs .vue-treeselect__menu {
    width: 95%;
    max-height: 160px;
    display: flex;
    flex-direction: column;
    overflow: auto;
  }

  .searchLogs .vue-treeselect__option {
    font-size: 14px;
  }

  .searchLogs .vue-treeselect__multi-value {
    margin: 3px 0px;
  }

  .searchLogs .vue-treeselect__multi-value-item-container {
    padding-top: 3px;
  }

  .searchLogs .iconFolder {
    display: inline-flex;
  }

  .searchLogs .vue-treeselect__checkbox--checked {
    background-color: #2985bf;
    border: 1px solid #2985bf;
  }

  .searchLogs .vue-treeselect__check-mark {
    margin: 0.5px;
  }

  .loader_loader_2-l8m {
    border: 5px solid #f5f5f5;
    border-top: 5px solid #2985bf;
    border-radius: 50%;
    width: 50px;
    height: 50px;
  }
</style>
